// Cross-platform demo audio. Mobile browsers (iOS Safari especially) only allow sound on elements first
// played inside a user tap, and ignore `audio.volume`. So: a fixed pool of channels unlocked on the first
// tap and reused for every sound, plus pre-attenuated files (`mixUrl`) so the mix level is baked in.
const BASE = process.env.REACT_APP_BACKEND_URL;
const SILENCE = "/silence.mp3";
const CHANNELS = ["voice", "music", "bed", "fx1", "fx2"];
const pool = {};
const gens = {};
let unlocked = false;

export const isIOS = typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

function el(name) {
  if (!pool[name]) {
    const a = new Audio();
    a.preload = "auto";
    a.setAttribute("playsinline", "");
    a.setAttribute("webkit-playsinline", "");
    pool[name] = a;
    gens[name] = 0;
  }
  return pool[name];
}

// Call synchronously inside a click/tap handler.
export function unlockAudio() {
  try { if (navigator.audioSession) navigator.audioSession.type = "playback"; } catch {}
  CHANNELS.forEach((name) => {
    const a = el(name);
    if (a.dataset.unlocked) return;
    if (!a.paused) { a.dataset.unlocked = "1"; return; }
    a.src = SILENCE;
    a.muted = false;
    const p = a.play();
    if (p) p.then(() => { a.dataset.unlocked = "1"; unlocked = true; if (a.src.endsWith(SILENCE)) a.pause(); }).catch(() => {});
  });
}

export const audioUnlocked = () => unlocked;

// Any tap/click/key anywhere unlocks the pool (capture phase, before the app's own handlers).
if (typeof document !== "undefined") {
  const onGesture = () => {
    unlockAudio();
    window.dispatchEvent(new CustomEvent("nex-audio-unlocked"));
  };
  ["click", "touchend", "keydown"].forEach((ev) => document.addEventListener(ev, onGesture, { capture: true, passive: true }));
}

// Bake a 0-1 level into a static audio file URL (server-side re-encode, cached).
export function mixUrl(url, level) {
  if (!url || level >= 1) return url;
  const m = url.match(/\/api\/static\/audio\/([^/?]+\.mp3)(\?[^#]*)?/);
  if (!m) return url;
  const pct = Math.max(1, Math.round(level * 100));
  return `${BASE}/api/audio-mix/${pct}/${m[1]}${m[2] || ""}`;
}

// Take a channel for one sound. Returns { el, play, release }; release only stops it if nobody re-took it.
export function takeChannel(name, url, { loop = false, volume = 1 } = {}) {
  const a = el(name);
  const gen = ++gens[name];
  a.onended = null; a.onerror = null; a.onloadedmetadata = null; a.ontimeupdate = null;
  a.pause();
  a.loop = loop;
  a.volume = volume;
  a.src = url;
  return {
    el: a,
    play: () => a.play().then(() => { a.dataset.unlocked = "1"; unlocked = true; }).catch((e) => {
      if (e?.name === "NotAllowedError") window.dispatchEvent(new CustomEvent("nex-audio-blocked"));
      throw e;
    }),
    owned: () => gens[name] === gen,
    release: () => {
      if (gens[name] !== gen) return;
      a.onended = null; a.onerror = null; a.onloadedmetadata = null; a.ontimeupdate = null;
      a.pause();
    },
  };
}
